import { useState, useRef, useEffect } from "react";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
} from "@/components/ui/SidePanel";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/AlertDialog";
import { Icon } from "@/components/ui/Icon";
import { cn } from "@/components/ui/cn";
import PollOptionInput, { type PollOptionInputRef } from "./PollOptionInput";

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 10;

// Duration options in hours (matches mobile)
export const POLL_DURATION_OPTIONS = [
  { label: "1 hour", hours: 1 },
  { label: "6 hours", hours: 6 },
  { label: "24 hours", hours: 24 },
  { label: "3 days", hours: 72 },
  { label: "7 days", hours: 168 },
];

export interface PollData {
  options: string[];
  durationHours: number;
}

interface PollEditorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: PollData) => void;
  onRemove: () => void;
  initialData?: PollData | null;
}

/**
 * Modal for creating/editing a poll.
 * Matches mobile PollEditor functionality with web-native UI.
 */
export function PollEditorModal({
  open,
  onOpenChange,
  onSave,
  onRemove,
  initialData,
}: PollEditorModalProps) {
  const [options, setOptions] = useState<string[]>(
    initialData?.options || ["", ""]
  );
  const [durationHours, setDurationHours] = useState<number>(
    initialData?.durationHours || 24
  );
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const optionRefs = useRef<(PollOptionInputRef | null)[]>([]);

  // Reset state when modal opens with new data
  useEffect(() => {
    if (open) {
      setOptions(initialData?.options || ["", ""]);
      setDurationHours(initialData?.durationHours || 24);
    }
  }, [open, initialData]);

  // Get the display label for current duration
  const currentDurationLabel =
    POLL_DURATION_OPTIONS.find((d) => d.hours === durationHours)?.label ||
    `${durationHours} hours`;

  // Update a specific option
  const handleOptionChange = (index: number, text: string) => {
    const newOptions = [...options];
    newOptions[index] = text;
    setOptions(newOptions);
  };

  // Remove an option (only if more than MIN_OPTIONS)
  const handleRemoveOption = (index: number) => {
    if (options.length <= MIN_OPTIONS) return;
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
  };

  // Add a new option (only if less than MAX_OPTIONS)
  const handleAddOption = () => {
    if (options.length >= MAX_OPTIONS) return;
    const newIndex = options.length;
    setOptions([...options, ""]);
    // Focus the new input after state update
    setTimeout(() => {
      optionRefs.current[newIndex]?.focus();
    }, 50);
  };

  // Handle save - filter out empty options but require at least 2
  const handleSave = () => {
    const nonEmptyOptions = options.filter((o) => o.trim().length > 0);
    if (nonEmptyOptions.length < MIN_OPTIONS) {
      // Don't save if less than 2 valid options
      return;
    }
    onSave({
      options: nonEmptyOptions,
      durationHours,
    });
    onOpenChange(false);
  };

  // Handle remove poll with confirmation
  const handleRemovePoll = () => {
    setShowRemoveConfirm(true);
  };

  const confirmRemovePoll = () => {
    onRemove();
    setShowRemoveConfirm(false);
    onOpenChange(false);
  };

  // Check if we can save (at least 2 non-empty options)
  const validOptionsCount = options.filter((o) => o.trim().length > 0).length;
  const canSave = validOptionsCount >= MIN_OPTIONS;

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelHeader title="Add a poll" onClose={() => onOpenChange(false)} />

        <SidePanelBody className="space-y-2">
          {/* Poll options */}
          {options.map((option, index) => (
            <PollOptionInput
              key={index}
              ref={(el) => { optionRefs.current[index] = el; }}
              value={option}
              onChangeText={(text) => handleOptionChange(index, text)}
              onRemove={() => handleRemoveOption(index)}
              showRemoveButton={options.length > MIN_OPTIONS}
              placeholder={`Option ${index + 1}`}
            />
          ))}

          {/* Add another option button */}
          {options.length < MAX_OPTIONS && (
            <PollOptionInput
              value=""
              onChangeText={() => {}}
              isAddButton
              onAddPress={handleAddOption}
            />
          )}

          {/* Duration selector and remove button */}
          <div className="pt-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                // Cycle through duration options
                const currentIndex = POLL_DURATION_OPTIONS.findIndex(
                  (d) => d.hours === durationHours
                );
                const nextIndex = (currentIndex + 1) % POLL_DURATION_OPTIONS.length;
                setDurationHours(POLL_DURATION_OPTIONS[nextIndex].hours);
              }}
              className="flex items-center gap-1.5 px-2 py-2 rounded-lg text-body-3 text-text-secondary hover:bg-surface-1 transition-colors"
            >
              <Icon name="clock" size={14} />
              <span>{currentDurationLabel}</span>
              <Icon name="chevron-down" size={10} className="text-text-tertiary" />
            </button>

            {initialData && (
              <button
                type="button"
                onClick={handleRemovePoll}
                className="px-2 py-2 text-body-3 text-text-tertiary hover:text-text-secondary transition-colors"
              >
                Remove poll
              </button>
            )}
          </div>
        </SidePanelBody>

        {/* Footer with buttons */}
        <SidePanelFooter className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-3 rounded-full text-body-2-medium text-text-secondary hover:bg-surface-2 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className={cn(
              "px-4 py-3 rounded-full text-body-2-medium transition-opacity",
              canSave
                ? "bg-olive text-white hover:opacity-90"
                : "bg-olive/50 text-white/70 cursor-not-allowed"
            )}
          >
            Done
          </button>
        </SidePanelFooter>
      </SidePanelContent>

      {/* Remove poll confirmation dialog */}
      <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Poll</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this poll?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-3 pt-4">
            <AlertDialogCancel className="w-full h-auto py-2 rounded-full">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="w-full h-auto py-2 rounded-full bg-red-600 hover:bg-red-700"
              onClick={confirmRemovePoll}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidePanel>
  );
}
